import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import useGameStore from '@/store/gameStore';
import apiService from '@/services/api';

function Lobby() {
  const navigate = useNavigate();
  const { user, setLoading } = useGameStore();
  const [roomCode, setRoomCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  const handleCreateRoom = async () => {
    setIsCreating(true);
    setLoading(true);
    try {
      const { roomId, roomCode } = await apiService.createRoom();
      toast.success(`Room created! Code: ${roomCode}`);
      navigate(`/room/${roomId}`);
    } catch (error) {
      toast.error('Failed to create room');
      console.error('Create room error:', error);
    } finally {
      setIsCreating(false);
      setLoading(false);
    }
  };

  const handleJoinRoom = async () => {
    if (roomCode.length !== 6) {
      toast.error('Room code must be 6 characters');
      return;
    }

    setIsJoining(true);
    setLoading(true);
    try {
      const { roomId } = await apiService.joinRoom(roomCode.toUpperCase());
      toast.success('Joining room...');
      navigate(`/room/${roomId}`);
    } catch (error) {
      toast.error('Failed to join room. Check the code and try again.');
      console.error('Join room error:', error);
    } finally {
      setIsJoining(false);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-lg w-full"
      >
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold font-game text-primary-600">Welcome back!</h1>
              <p className="text-gray-600 mt-1">{user?.name || 'Player'}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              data-testid="btn-logout"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Create Room Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-700 mb-3">Create a New Room</h2>
              <button
                onClick={handleCreateRoom}
                disabled={isCreating || isJoining}
                data-testid="btn-create-room"
                className="w-full btn-primary"
              >
                {isCreating ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⚡</span> Creating...
                  </span>
                ) : (
                  'Create New Room'
                )}
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">OR</span>
              </div>
            </div>

            {/* Join Room Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-700 mb-3">Join Existing Room</h2>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  disabled={isCreating || isJoining}
                  data-testid="input-room-code"
                  className="input flex-1 font-mono text-lg tracking-wider uppercase"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleJoinRoom();
                    }
                  }}
                />
                <button
                  onClick={handleJoinRoom}
                  disabled={isCreating || isJoining || roomCode.length !== 6}
                  data-testid="btn-join-room"
                  className="btn-secondary px-8"
                >
                  {isJoining ? <span className="animate-pulse">Joining...</span> : 'Join'}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 bg-primary-50 rounded-lg">
            <h3 className="font-semibold text-primary-700 mb-2">Quick Tips:</h3>
            <ul className="text-sm text-primary-600 space-y-1">
              <li>• Rooms need at least 3 players to start</li>
              <li>• You can add up to 5 AI players</li>
              <li>• Games last 13 rounds by default</li>
              <li>• Share the room code with friends to play together!</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default Lobby;
